import type { UserConfidence } from "@cloudox/api-client";

/**
 * User-facing confidence label (Verified / Likely / Assumed / Unknown). Shown
 * where confidence changes the reader's action — not on every sentence. The
 * per-view display policy decides how often it appears; this component only
 * renders one consistently.
 */
export interface ConfidenceBadgeProps {
  readonly confidence: UserConfidence;
  /** Compact dot+label for dense rows; default shows the full pill. */
  readonly size?: "sm" | "md";
  readonly className?: string;
}

const TONE: Record<UserConfidence, { color: string; bg: string }> = {
  Verified: { color: "var(--success)", bg: "color-mix(in oklab, var(--success) 14%, transparent)" },
  Likely: { color: "var(--accent)", bg: "var(--accent-muted)" },
  Assumed: { color: "var(--warning)", bg: "color-mix(in oklab, var(--warning) 16%, transparent)" },
  Unknown: { color: "var(--fg-muted)", bg: "var(--bg-muted)" },
};

export function ConfidenceBadge({ confidence, size = "md", className = "" }: ConfidenceBadgeProps) {
  const tone = TONE[confidence];
  return (
    <span
      className={`badge ${className}`.trim()}
      style={{ color: tone.color, backgroundColor: tone.bg }}
      title={`Confidence: ${confidence}`}
    >
      <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tone.color }} />
      {size === "md" ? confidence : null}
      <span className="sr-only">Confidence {confidence}</span>
    </span>
  );
}
